import { EncField, Encoding } from '@mode-vegas/vegas';
import { Attr, Field, Model } from '@mode-switch/model';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { Switch } from '@mode/shared/util-switch';
import { escapeFieldName } from '@mode/shared/util-js';
import { ExprFrame, ExprStage } from '@mode-switch/express';

type ModeRole = FlamingoTypes.ModeRole;

export function makeField(
  model: Model<ModeRole>,
  viewName: string,
  encoding: Encoding,
  fields: Map<string, Field<ModeRole>>,
  encField?: EncField
): Field<ModeRole> {
  if (encField == null) {
    throw Error(`EncField can not be undefined`);
  }

  if (encField.id == null) {
    throw new Error(`EncField with formula ${encField.formula} has no id`);
  }

  let field = fields.get(encField.id);
  if (field == null) {
    field = Switch.field(
      model,
      viewName,
      encField.formula,
      Switch.Compat.fromVegasVarType(encField.variable.type),
      encField.id,
      encField.frame
    );
  }

  if (field == null) {
    throw Error(`Field with formula ${encField.formula} does not exist in view named ${viewName}`);
  }

  /**
   * @todo Figure out where this should live long term. We need to create the frame
   * in a way that is not persisted to the encoding; so it exists here where we build
   * fields for queries. We're patching Switch's default frame assignment as a result
   * of keeping series on their position channel, keeping us backward compatible with
   * existing quick charts in production. Viz Explorer allows one to choose a frame
   * from the Compute Using dropdown menu, while quick charts do not
   *
   * see: https://github.com/mode/vegas-switch/blob/d71eeba161979590fdbb13f8d3354648376b899f/packages/process/source/ProcessField.ts#L154-L161
   *
   * if (x && !y) TableAcross
   * else TableDown
   */
  if (field.source !== Attr.Source.Values && field.role === Attr.Role.Measure && encField.frame == null) {
    const expr = Switch.compileToExpr(model, viewName, field.formula.source);
    if (expr?.stage === ExprStage.Analytic) {
      let frame: ExprFrame;

      switch (encoding.marks.channel) {
        case 'x': {
          /**
           * Previous query structure
           * ---
           * - Series from x goes in marks
           * - Add all y fields, if any
           *
           * Always TableDown
           */
          frame = ExprFrame.Table.make(ExprFrame.Address.Down);
          break;
        }
        case 'y': {
          /**
           * Previous query structure
           * ---
           * - Series from y goes in marks
           * - Add all x fields, if any
           *
           * TableAcross: if we added fields from x
           * TableDown: no fields on x
           */
          const numOnX = encoding.x.length + encoding.x2.length;
          if (numOnX > 0) {
            frame = ExprFrame.Table.make(ExprFrame.Address.Across);
          } else {
            frame = ExprFrame.Table.make(ExprFrame.Address.Down);
          }
          break;
        }
        case '*': {
          /**
           * Current/Previous query structure
           * ---
           * - Add all x fields, if any
           * - Add all y fields, if any
           *
           * * if (x && !y) TableAcross
           * * else TableDown
           */
          const numOnX = encoding.x.length + encoding.x2.length;
          const numOnY = encoding.y.length + encoding.y2.length;
          if (numOnX > 0 && numOnY === 0) {
            frame = ExprFrame.Table.make(ExprFrame.Address.Across);
          } else {
            frame = ExprFrame.Table.make(ExprFrame.Address.Down);
          }
          break;
        }
      }

      field = {
        ...field,
        frame,
      };
    }
  }

  fields.set(encField.id, field);

  return { ...field, formula: { ...field.formula, source: escapeFieldName(field.formula.source) } } as Field<ModeRole>;
}
